import React, { useLayoutEffect, useId, useRef, useState } from 'react';
import { localeRegion } from '../utils/localeFlags';

type Option = { value: string; label: string; search?: string };
type Props = { label: string; value: string; onChange: (value: string) => void; kind: 'language' | 'region'; options: Option[] };

function russianName(value: string, kind: Props['kind']): string {
  try {
    const DisplayNames = (Intl as unknown as { DisplayNames: new (locales: string[], options: { type: string }) => { of: (code: string) => string | undefined } }).DisplayNames;
    const code = kind === 'language' ? value.replace('_', '-') : value === 'uk' ? 'GB' : value.split('_')[0].toUpperCase();
    return new DisplayNames(['ru'], { type: kind }).of(code) || '';
  } catch { return ''; }
}

const normalizeSearch = (text: string) => text.toLocaleLowerCase('ru').replace(/ё/g, 'е').trim();

function Flag({ code, kind }: { code: string; kind: Props['kind'] }) {
  const region = localeRegion(code, kind);
  if (region === 'international') return <svg className="wp-flag" viewBox="0 0 24 24" aria-hidden="true"><g stroke="currentColor" fill="none" strokeWidth="1.5"><circle cx="12" cy="12" r="9"/><ellipse cx="12" cy="12" rx="4" ry="9"/><path d="M3 12h18"/></g></svg>;
  return <img className="wp-flag" src={`/flags/${region}.svg`} alt="" width="24" height="18" />;
}

export default function LocaleSelect({ label, value, onChange, kind, options }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [above, setAbove] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const selected = options.find(option => option.value === value);
  const filtered = options.filter(option => normalizeSearch(`${option.label} ${option.search || ''} ${option.value} ${russianName(option.value, kind)}`).includes(normalizeSearch(query)));
  const close = (restoreFocus = false) => { setOpen(false); if (restoreFocus) trigger.current?.focus(); };
  useLayoutEffect(() => {
    if (!open) return;
    const rect = root.current?.getBoundingClientRect();
    setAbove(Boolean(rect && window.innerHeight - rect.bottom < 330 && rect.top > 330));
    search.current?.focus({ preventScroll: true });
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);
  const focusOption = (index: number) => {
    const buttons = root.current?.querySelectorAll<HTMLButtonElement>('[role="option"]');
    if (buttons?.length) buttons[(index + buttons.length) % buttons.length].focus();
  };
  return <div className="wp-localeSelect" ref={root}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) close(); }}
    onKeyDown={event => { if (event.key === 'Escape' && open) { event.preventDefault(); event.stopPropagation(); close(true); } }}>
    <button ref={trigger} type="button" className="u2-select wp-localeTrigger" role="combobox"
      aria-label={label} aria-expanded={open} aria-controls={`${id}-options`} aria-haspopup="listbox"
      onClick={() => { setQuery(''); setOpen(!open); }}
      onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setQuery(''); setOpen(true); } }}>
      <Flag code={value} kind={kind} /><span>{selected?.label || 'Выберите значение'}</span><span className="wp-selectArrow" aria-hidden="true">⌄</span>
    </button>
    {open && <div className={`wp-localePopup ${above ? 'is-above' : ''}`}>
      <input ref={search} className="u2-input" aria-label={`Поиск: ${label}`} placeholder={kind === 'language' ? 'Название или код языка' : 'Название или код региона'} value={query}
        onChange={event => setQuery(event.target.value)}
        onKeyDown={event => { if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); focusOption(event.key === 'ArrowDown' ? 0 : filtered.length - 1); } }} />
      <div id={`${id}-options`} role="listbox" aria-label={label} className="wp-localeOptions">
        {filtered.map((option, index) => <button key={option.value} type="button" role="option" aria-selected={option.value === value}
          className="wp-localeOption" onClick={() => { onChange(option.value); close(true); }}
          onKeyDown={event => {
            if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
              event.preventDefault(); focusOption(event.key === 'Home' ? 0 : event.key === 'End' ? filtered.length - 1 : index + (event.key === 'ArrowDown' ? 1 : -1));
            }
          }}><Flag code={option.value} kind={kind} /><span>{option.label}</span><span aria-hidden="true">{option.value === value ? '✓' : ''}</span></button>)}
      </div>
      {filtered.length === 0 && <p className="u2-muted" role="status">Ничего не найдено. Измените запрос.</p>}
    </div>}
  </div>;
}
